import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Avatar,
  Divider,
  Paper,
  InputAdornment,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Gavel as LawyerIcon
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useTranslations } from '../../constants/translations/CaseTranslation';

export default function ChatModal({ 
  open, 
  onClose, 
  caseId, 
  currentUserId, 
  currentUserRole, 
  officeColor, 
  caseData,
  caseLanguage = 'spanish',
  caseMembers = [],
}) {
  const t = useTranslations(caseLanguage);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [participants, setParticipants] = useState({});
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && caseData && caseMembers.length > 0) { 
      loadParticipants();
      loadMessages();
      setTimeout(() => inputRef.current?.focus(), 100);
      
      const channel = supabase
        .channel(`chat-${caseId}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'message',
          filter: `case_id=eq.${caseId}`
        }, payload => {
          setMessages(prev => [...prev, payload.new]);
        })
        .subscribe();

      return () => supabase.removeChannel(channel);
    }
  }, [open, caseId, caseData, caseMembers]); 

  const loadParticipants = async () => {
    if (!caseMembers || caseMembers.length === 0) return;

    const participantMap = {};
    
    // Agregar miembros del caso desde caseMembers
    caseMembers.forEach(member => {
      const participantId = member.person_id;
      
      if (participantId) {
        participantMap[participantId] = {
          id: participantId,
          full_name: member.full_name?.replace(/;/g, '') || t.user,
          email: member.email,
          role: member.role // 'client', 'lawyer', o 'partner'
        };
      }
    });

    // Cargar todos los usuarios que hayan enviado mensajes pero NO estén en case_members
    const memberIds = caseMembers
      .filter(m => m.person_id)
      .map(m => m.person_id);

    if (memberIds.length > 0) {
      const { data: allSenders, error: sendersError } = await supabase
        .from('message')
        .select(`
          sender_id,
          sender:person!sender_id(id, full_name, email)
        `)
        .eq('case_id', caseId)
        .not('sender_id', 'in', `(${memberIds.join(',')})`);

      if (sendersError) {
        console.error('Error loading message senders:', sendersError);
      }

      // Agregar otros usuarios (admins u otros) que han participado
      if (allSenders && allSenders.length > 0) {
        const uniqueSenders = allSenders.reduce((acc, msg) => {
          if (msg.sender && !acc[msg.sender.id]) {
            acc[msg.sender.id] = {
              ...msg.sender,
              role: 'admin', // Asumimos que son administradores
              full_name: msg.sender.full_name?.replace(/;/g, '') || t.administrator
            };
          }
          return acc;
        }, {});

        Object.assign(participantMap, uniqueSenders);
      }
    } else {
      // Si no hay miembros con person_id, cargar todos los senders
      const { data: allSenders, error: sendersError } = await supabase
        .from('message')
        .select(`
          sender_id,
          sender:person!sender_id(id, full_name, email)
        `)
        .eq('case_id', caseId);

      if (!sendersError && allSenders) {
        allSenders.forEach(msg => {
          if (msg.sender && !participantMap[msg.sender.id]) {
            participantMap[msg.sender.id] = {
              ...msg.sender,
              role: 'admin',
              full_name: msg.sender.full_name?.replace(/;/g, '') || t.administrator
            };
          }
        });
      }
    }

    setParticipants(participantMap);
  };

  // Función simplificada - todos los mensajes del caso son visibles
  const loadMessages = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('message')
      .select(`
        *,
        sender:person!sender_id(id, full_name, email)
      `)
      .eq('case_id', caseId)
      .order('created_at', { ascending: true });

    if (!error) {
      setMessages(data || []);
    } else {
      console.error('Error loading messages:', error);
    }
    setLoading(false);
  };

  // Función simplificada - sin receiver_id
  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    const messageText = newMessage.trim();
    setNewMessage('');

    try {
      // Estructura simplificada del mensaje - solo case_id, sender_id y message
      const messageData = {
        case_id: caseId,
        sender_id: currentUserId,
        message: messageText
      };

      const { error } = await supabase.from('message').insert(messageData);
      
      if (error) throw error;

    } catch (error) {
      console.error('Error sending message:', error);
      setNewMessage(messageText); // Restaurar mensaje en caso de error
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const locale = caseLanguage === 'english' ? 'en-US' : 'es-ES';
    
    if (isToday) {
      return date.toLocaleTimeString(locale, { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
    
    return date.toLocaleDateString(locale, { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach(msg => {
      const date = new Date(msg.created_at).toDateString();
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });
    return groups;
  };

  const getMessageSender = (msg) => {
    // Usar la información del sender si está disponible
    if (msg.sender) {
      return {
        name: msg.sender.full_name?.replace(/;/g, '') || t.user,
        role: getParticipantRole(msg.sender_id)
      };
    }
    
    // Fallback a participants si no hay sender en el mensaje
    const participant = participants[msg.sender_id];
    if (participant) {
      return {
        name: participant.full_name,
        role: participant.role
      };
    }

    return {
      name: t.user,
      role: 'unknown'
    };
  };

  const getParticipantRole = (userId) => {
  // Buscar en los miembros del caso primero
    const caseMember = caseMembers.find(m => m.person_id === userId);
    if (caseMember) {
      return caseMember.role; // 'client', 'lawyer', o 'partner'
    }
    
    // Si no está en case_members, asumir que es admin
    return 'admin';
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <AdminIcon fontSize="small" />;
      case 'lawyer':
        return <LawyerIcon fontSize="small" />;
      case 'partner':
        return <LawyerIcon fontSize="small" />; // Mismo ícono que lawyer
      case 'client':
        return <PersonIcon fontSize="small" />;
      default:
        return <PersonIcon fontSize="small" />;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return t.administrator || 'Administrador';
      case 'lawyer':
        return t.lawyer || 'Abogado Principal';
      case 'partner':
        return t.attorney || 'Procurador'; // Colaborador/Procurador
      case 'client':
        return t.client || 'Cliente';
      default:
        return t.user || 'Usuario';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return '#ff9800';
      case 'lawyer':
        return officeColor || '#1976d2';
      case 'partner':
        return '#9c27b0'; // Color distintivo para procuradores
      case 'client':
        return '#4caf50';
      default:
        return '#9e9e9e';
    }
  };

  const getCurrentUserRoleLabel = () => {
    return getRoleLabel(currentUserRole);
  };

  const formatDateSeparator = (dateString) => {
    const date = new Date(dateString);
    const locale = caseLanguage === 'english' ? 'en-US' : 'es-ES';
    
    return date.toLocaleDateString(locale, { 
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  const groupedMessages = groupMessagesByDate(messages);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
      PaperProps={{
        sx: {
          height: fullScreen ? '100%' : '88vh',
          maxHeight: fullScreen ? '100%' : '900px',
          borderRadius: fullScreen ? 0 : 3,
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          backgroundColor: officeColor || '#1976d2',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
          {getRoleIcon(currentUserRole)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t.caseChat}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {t.participatingAs} {getCurrentUserRoleLabel()}
          </Typography>
        </Box>
        <IconButton 
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </Paper>

      {/* Messages Area */}
      <DialogContent 
        sx={{ 
          p: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            flex: 1,
            overflowY: 'auto',
            p: 2,
            backgroundColor: '#f8fafc'
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {Object.entries(groupedMessages).map(([date, dayMessages]) => (
                <Box key={date}>
                  {/* Date Separator */}
                  <Box display="flex" justifyContent="center" my={2}>
                    <Chip 
                      label={formatDateSeparator(date)}
                      size="small"
                      sx={{ 
                        backgroundColor: 'rgba(0,0,0,0.05)',
                        color: 'text.secondary',
                        textTransform: 'capitalize'
                      }}
                    />
                  </Box>

                  {/* Messages for this date */}
                  {dayMessages.map((msg, index) => {
                    const isCurrentUser = msg.sender_id === currentUserId;
                    const sender = getMessageSender(msg);
                    const showAvatar = !isCurrentUser && (
                      index === 0 || 
                      dayMessages[index - 1]?.sender_id !== msg.sender_id
                    );
                    const showSenderName = !isCurrentUser && showAvatar;

                    return (
                      <Box
                        key={msg.id}
                        sx={{
                          display: 'flex',
                          mb: 1,
                          alignItems: 'flex-end',
                          justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                          gap: 1
                        }}
                      >
                        {/* Avatar para mensajes de otros usuarios */}
                        {!isCurrentUser && (
                          <Avatar 
                            sx={{ 
                              width: 32, 
                              height: 32,
                              visibility: showAvatar ? 'visible' : 'hidden',
                              backgroundColor: getRoleColor(sender.role)
                            }}
                          >
                            {getRoleIcon(sender.role)}
                          </Avatar>
                        )}

                        {/* Message bubble */}
                        <Box sx={{ maxWidth: { xs: '85%', sm: '75%' } }}>
                          {/* Sender name for non-current users */}
                          {showSenderName && (
                            <Typography
                              variant="body2"
                              sx={{
                                ml: 1,
                                color: getRoleColor(sender.role),
                                fontWeight: 'bold',
                                fontSize: '0.85rem',
                                display: 'block',
                                mb: 0.5
                              }}
                            >
                              {getRoleLabel(sender.role)} • {sender.name}
                            </Typography>
                          )}

                          <Paper
                            elevation={1}
                            sx={{
                              p: 1.75,
                              backgroundColor: isCurrentUser
                                ? officeColor || '#1976d2'
                                : 'white',
                              color: isCurrentUser ? 'white' : 'text.primary',
                              borderRadius: 2,
                              borderBottomRightRadius: isCurrentUser ? 0.5 : 2,
                              borderBottomLeftRadius: !isCurrentUser ? 0.5 : 2,
                              position: 'relative'
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                wordBreak: 'break-word',
                                lineHeight: 1.5,
                                fontSize: '0.95rem'
                              }}
                            >
                              {msg.message}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                opacity: 0.8,
                                fontSize: '0.75rem',
                                mt: 0.5,
                                display: 'block'
                              }}
                            >
                              {formatTime(msg.created_at)}
                            </Typography>
                          </Paper>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ))}
              
              {messages.length === 0 && !loading && (
                <Box 
                  display="flex" 
                  flexDirection="column" 
                  alignItems="center" 
                  justifyContent="center" 
                  py={8}
                  sx={{ color: 'text.secondary' }}
                >
                  <EmojiIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" gutterBottom>
                    {t.startGroupConversation}
                  </Typography>
                  <Typography variant="body2">
                    {t.allParticipantsCanSee}
                  </Typography>
                </Box>
              )}
            </>
          )}
          <div ref={bottomRef} />
        </Box>

        <Divider />

        {/* Input Area */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 2,
            backgroundColor: 'white',
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              ref={inputRef}
              fullWidth
              multiline
              maxRows={4}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t.writeMessagePlaceholder}
              variant="outlined"
              disabled={sending}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  fontSize: '0.95rem',
                  backgroundColor: '#f8fafc',
                  '&:hover': {
                    backgroundColor: '#f1f5f9'
                  }
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {/* <IconButton size="small" disabled>
                      <EmojiIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" disabled>
                      <AttachFileIcon fontSize="small" />
                    </IconButton> */}
                  </InputAdornment>
                )
              }}
            />
            <Button
              onClick={handleSend}
              variant="contained"
              disabled={!newMessage.trim() || sending}
              sx={{ 
                backgroundColor: officeColor || '#1976d2',
                borderRadius: 3,
                minWidth: 48,
                height: 48,
                '&:hover': {
                  backgroundColor: officeColor ? `${officeColor}dd` : '#1565c0'
                }
              }}
            >
              {sending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SendIcon />
              )}
            </Button>
          </Box>
          
          {/* Typing indicator area */}
          <Box sx={{ height: 20, mt: 1 }}>
            {sending && (
              <Typography variant="caption" color="text.secondary">
                {t.sending}
              </Typography>
            )}
          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  );
}